import { UnitType } from "src/store/api/generatedApi";

// A yard is counted in the larger unit even where the recipes drawing on it are
// written in pounds: pounds stop at the recipe. Every surface showing inventory
// reads this rather than the measure its own table is in.
export const INVENTORY_UNIT: UnitType = "mass";

export interface RoundedInventory<Key extends string | number> {
  byMaterial: Map<Key, number>;
  total: number;
}

// Inventory as it is displayed: each material rounded to whole units, and the
// total of exactly those rounded numbers.
//
// The pair belongs together. A total summed from unrounded values, or rounded
// once at the end, is a figure no column on screen adds up to — a tonne out on
// a period the moment more than a couple of materials carry a fraction — and
// two surfaces that round differently disagree about the same yard.
//
// `parseFloat` on the stringified value because the field is typed `float` but
// arrives over JSON: a string would make `+` concatenate, and the total would
// be nonsense rather than merely wrong.
export const roundedInventory = <Summary, Key extends string | number>({
  summaries,
  material,
  quantity,
}: {
  summaries: Summary[];
  material: (summary: Summary) => Key;
  quantity: (summary: Summary) => number;
}): RoundedInventory<Key> => {
  const byMaterial = new Map(
    summaries.map((summary) => [
      material(summary),
      Math.round(parseFloat(quantity(summary).toString())),
    ])
  );
  const total = [...byMaterial.values()].reduce(
    (running, value) => running + value,
    0
  );
  return { byMaterial, total };
};
